import LookupAnalyticsDashboardViewSearchSection, {LookupAnalyticsDateOptionRanges} from "../../../../../../components/search-sections/lookup-analytics/dashboard";
import useQuery from "../../../../../../hooks/use-query";
import {Box, Button} from "@mui/material";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import DateUtils from "../../../../../../../core/services/date-utils";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {LayersRounded} from "@mui/icons-material";
import Grid2 from "@mui/material/Unstable_Grid2";
import ProductInformationPopover from "../../../../../../components/app-specific/draggable-popover/product-information-popover";
import {routes} from "../../../../../../../core/models/static/routes/app-routes";
import RearrangeEntriesDialog from "../../../../../../components/dialogs/rearrange-entries";
import Api from "../../../../../../../core/services/api/api";
import LoadingLottieAnimation from "../../../../../../components/app-specific/lottie-animations/loading";
import LookupAnalyticsDashboardViewChart from "./chart";
import LookupAnalyticsDashboardViewTable from "./table";
import useSavedComponentsState from "../../../../../../hooks/use-saved-components-state";

const chartIdentifiers = {
    overview: 'overview',
}


const tableIdentifiers = {
    topQueries: 'top-queries',
    topQueriesWithResults: 'top-queries-w-results',
    topQueriesWithClicks: 'top-queries-w-clicks',
    topQueriesWithoutResults: 'top-queries-w-clicks-w-results',
    topClickedProducts: 'top-clicked-products',
    customersWithHighestQueries: 'customers-with-highest-queries',
    customersWithHighestClicks: 'customers-with-highest-clicks',
    customersWithHighestQueryClickRatio: 'customers-with-highest-clicks-w-results',
    operatorsWithHighestQueries: 'operators-with-highest-queries',
    operatorsWithHighestQueryClickRatio: 'operators-with-highest-clicks-w-results',
}

const initialRearrangeEntriesDialogState = {
    open: false,
    close: () => void 0,
    title: undefined,
    entries: [],
}

const LookupAnalyticsDashboardView = () => {
    const [query, setQuery] = useQuery();
    const [componentOrder, setComponentOrder] = useState({});
    const [rearrangeEntriesDialogState, setRearrangeEntriesDialogState] = useState(initialRearrangeEntriesDialogState)
    const [loadingComponentOrders, setLoadingComponentOrders] = useState(true);
    const {highestVersion: getCachedComponentOrders, save: saveCachedComponentOrders} = useSavedComponentsState({name: "lookup-analytics-dashboard-components-order", version: '1.0.0'});
    const cache = useRef({
        charts: Object.fromEntries(Object.entries(chartIdentifiers).map(([, value]) => [value, {}])),
        tables: Object.fromEntries(Object.entries(tableIdentifiers).map(([, value]) => [value, {}])),
    });
    const isMounted = useIsMounted();

    /**
     * Fetches the chart data based on the current query
     * @type {(function(*, *, *): Promise<*[] | undefined>)}
     */
    const fetchChartData = useCallback(async (identifier, forApi, cancelToken) => {
        /**@type{ApiResponseDs}*/
        let response;

        const filtersIdentifier = JSON.stringify(forApi);
        const cachedEntry = cache.current.charts[identifier][filtersIdentifier];
        if (cachedEntry && cachedEntry.cacheDateTime.diff(DateUtils.localNow(), 'hour') < 1) {
            return cachedEntry.data;
        }

        const defaultPeriod = LookupAnalyticsDateOptionRanges.last2Weeks();
        defaultPeriod.startDate = DateUtils.UTCToServer(defaultPeriod.startDate).format(DateUtils.withoutOffsetFormat);
        defaultPeriod.endDate = DateUtils.UTCToServer(defaultPeriod.endDate).format(DateUtils.withoutOffsetFormat);

        forApi.period = {
            ...forApi.period,
            startDateTime: forApi.period?.startDateTime ?? defaultPeriod.startDate,
            endDateTime: forApi.period?.endDateTime ?? defaultPeriod.endDate,
        }

        switch (identifier) {
            case chartIdentifiers.overview:
                response = await Api.getNexusSearchReportGeneralChart(forApi, cancelToken);
                break;
            default:
                return;
        }
        if (!isMounted())
            return;

        let data = response?.data ?? [];
        if (!Array.isArray(data)) {
            data = data?.items ?? [];
        }
        if (response?.resultFlag) {
            cache.current.charts[identifier][filtersIdentifier] = {
                data: data,
                cacheDateTime: DateUtils.localNow(),
            }
        }
        return data;
    }, [isMounted])

    /**
     * Fetches the table data based on the current query
     * @type {(function(*, *, *): Promise<*[] | undefined>)}
     */
    const fetchTableData = useCallback(async (identifier, forApi, cancelToken) => {
        /**@type{ApiResponseDs}*/
        let response;

        const filtersIdentifier = JSON.stringify(forApi);
        const cachedEntry = cache.current.tables[identifier][filtersIdentifier];
        if (cachedEntry && cachedEntry.cacheDateTime.diff(DateUtils.localNow(), 'hour') < 1) {
            return cachedEntry.data;
        }

        const defaultPeriod = LookupAnalyticsDateOptionRanges.last2Weeks();
        defaultPeriod.startDate = DateUtils.UTCToServer(defaultPeriod.startDate).format(DateUtils.withoutOffsetFormat);
        defaultPeriod.endDate = DateUtils.UTCToServer(defaultPeriod.endDate).format(DateUtils.withoutOffsetFormat);

        forApi.period = {
            ...forApi.period,
            startDateTime: forApi.period?.startDateTime ?? defaultPeriod.startDate,
            endDateTime: forApi.period?.endDateTime ?? defaultPeriod.endDate,
        }

        switch (identifier) {
            case tableIdentifiers.topQueries:
                response = await Api.getNexusSearchReportTopQueries(forApi, cancelToken);
                break;
            case tableIdentifiers.topQueriesWithResults:
                response = await Api.getNexusSearchReportTopQueriesWithResult(forApi, cancelToken);
                break;
            case tableIdentifiers.topQueriesWithClicks:
                response = await Api.getNexusSearchReportTopQueriesLead2Click(forApi, cancelToken);
                break;
            case tableIdentifiers.topQueriesWithoutResults:
                response = await Api.getNexusSearchReportTopQueriesWithoutResult(forApi, cancelToken);
                break;
            case tableIdentifiers.topClickedProducts:
                response = await Api.getNexusSearchReportTopClickedProducts(forApi, cancelToken);
                break;
            case tableIdentifiers.customersWithHighestQueries:
                response = await Api.getNexusSearchReportTopCustomerHighSearch(forApi, cancelToken);
                break;
            case tableIdentifiers.customersWithHighestClicks:
                response = await Api.getNexusSearchReportTopCustomerHighClick(forApi, cancelToken);
                break;
            case tableIdentifiers.customersWithHighestQueryClickRatio:
                response = await Api.getNexusSearchReportTopCustomerHighQCRatio(forApi, cancelToken);
                break;
            case tableIdentifiers.operatorsWithHighestQueries:
                response = await Api.getNexusSearchReportTopOperatorsHighSearch(forApi, cancelToken);
                break;
            case tableIdentifiers.operatorsWithHighestQueryClickRatio:
                response = await Api.getNexusSearchReportTopOperatorsHighQCRatio(forApi, cancelToken);
                break;
            default:
                return;
        }
        if (!isMounted())
            return;
        let data = response?.data ?? [];
        if (!Array.isArray(data)) {
            data = data?.items ?? [];
        }
        if (response?.resultFlag) {
            cache.current.tables[identifier][filtersIdentifier] = {
                data: data,
                cacheDateTime: DateUtils.localNow(),
            }
        }
        return data;
    }, [isMounted])

    /**
     * Navigates to the appropriate page based on the identifier
     * @type {(function(*, *): void)}
     */
    const navigateOnClick = useCallback((identifier, entry) => {
        const newQuery = {};
        switch (identifier) {
            case tableIdentifiers.topQueries:
                newQuery.keyword = entry.keyword;
                break;
            case tableIdentifiers.topQueriesWithResults:
                newQuery.keyword = entry.keyword;
                break;
            case tableIdentifiers.topQueriesWithClicks:
                newQuery.keyword = entry.keyword;
                break;
            case tableIdentifiers.topQueriesWithoutResults:
                newQuery.keyword = entry.keyword;
                break;
            case tableIdentifiers.topClickedProducts:
                newQuery.part = {
                    partNo: entry.partNumber,
                    id: entry.partId,
                }
                break;
            case tableIdentifiers.customersWithHighestQueries:
                newQuery.customer = {id: entry.customerId, name: entry.customerName};
                break;
            case tableIdentifiers.customersWithHighestClicks:
                newQuery.customer = {id: entry.customerId, name: entry.customerName};
                break;
            case tableIdentifiers.customersWithHighestQueryClickRatio:
                newQuery.customer = {id: entry.customerId, name: entry.customerName};
                break;
            case tableIdentifiers.operatorsWithHighestQueries:
                newQuery.operator = {id: entry.operatorId, firstName: entry.operatorName.split(' ')[0], lastName: entry.operatorName.split(' ').slice(1).join(' ')};
                break;
            case tableIdentifiers.operatorsWithHighestQueryClickRatio:
                newQuery.operator = {id: entry.operatorId, firstName: entry.operatorName.split(' ')[0], lastName: entry.operatorName.split(' ').slice(1).join(' ')};
                break;
            default:
                return;
        }
        setQuery(prevState => ({
            industryId: prevState.industryId,
            startDate: prevState.startDate,
            endDate: prevState.endDate,
            ...newQuery
        }), {location: {pathname: routes.analytics.lookup.logs}});
    }, [setQuery])

    const chartDefinitions = useMemo(() => ({
        [chartIdentifiers.overview]: {
            identifier: chartIdentifiers.overview,
            title: "Overview",
            datasetEntries: {
                numberOfQueries: {title: 'Total Queries',},
                numberOfClicked: {title: 'Total Lost Search',},
                numberOfLostSearch: {title: 'Total Click',},
            },
        }
    }), [])

    const tableDefinitions = useMemo(() => ({
        [tableIdentifiers.topQueries]: {
            identifier: tableIdentifiers.topQueries,
            title: 'Top Queries',
            columnDefinitions: {
                keyword: {title: 'Keywords', orderIndex: 1},
                repeat: {title: 'Repeat No', orderIndex: 2},
            },
        },
        [tableIdentifiers.topQueriesWithResults]: {
            identifier: tableIdentifiers.topQueriesWithResults,
            title: 'Top Queries with Results',
            columnDefinitions: {
                keyword: {title: 'Keywords', orderIndex: 1},
                repeat: {title: 'Repeat No', orderIndex: 2},
                avgResultCount: {title: 'Avg Results', orderIndex: 3},
            },
        },
        [tableIdentifiers.topQueriesWithClicks]: {
            identifier: tableIdentifiers.topQueriesWithClicks,
            title: 'Top Queries with Clicks',
            columnDefinitions: {
                keyword: {title: 'Keywords', orderIndex: 1},
                repeat: {title: 'Repeat No', orderIndex: 2},
                avgResultCount: {title: 'Avg Results', orderIndex: 3},
            },
        },
        [tableIdentifiers.topQueriesWithoutResults]: {
            identifier: tableIdentifiers.topQueriesWithoutResults,
            title: 'Top Queries Without Results',
            columnDefinitions: {
                keyword: {title: 'Keywords', orderIndex: 1},
                repeat: {title: 'Repeat No', orderIndex: 2},
            },
        },
        [tableIdentifiers.topClickedProducts]: {
            identifier: tableIdentifiers.topClickedProducts,
            title: 'Most Clicked Products',
            columnDefinitions: {
                partNumber: {title: 'Part No', orderIndex: 1, render: (e) => <ProductInformationPopover partNo={e.partNumber}>{e.partNumber ?? ""}</ProductInformationPopover>},
                numberOfClicked: {title: 'Click No', orderIndex: 2},
            },
        },
        [tableIdentifiers.customersWithHighestQueries]: {
            identifier: tableIdentifiers.customersWithHighestQueries,
            title: 'Customers with Highest Queries',
            columnDefinitions: {
                customerName: {title: 'Customer', orderIndex: 1},
                numberOfQueries: {title: 'Query No', orderIndex: 2},
            },
        },
        [tableIdentifiers.customersWithHighestClicks]: {
            identifier: tableIdentifiers.customersWithHighestClicks,
            title: 'Customers with Highest Clicks',
            columnDefinitions: {
                customerName: {title: 'Customer', orderIndex: 1},
                numberOfClicked: {title: 'Click No', orderIndex: 2},
            },
        },
        [tableIdentifiers.customersWithHighestQueryClickRatio]: {
            identifier: tableIdentifiers.customersWithHighestQueryClickRatio,
            title: 'Customers with Highest Query/Click Ratio',
            columnDefinitions: {
                customerName: {title: 'Customer', orderIndex: 1},
                ratio: {title: 'Ratio', orderIndex: 2},
            },
        },
        [tableIdentifiers.operatorsWithHighestQueries]: {
            identifier: tableIdentifiers.operatorsWithHighestQueries,
            title: 'Operators with Highest Queries',
            columnDefinitions: {
                operatorName: {title: 'Operator', orderIndex: 1},
                numberOfQueries: {title: 'Query No', orderIndex: 2},
            },
        },
        [tableIdentifiers.operatorsWithHighestQueryClickRatio]: {
            identifier: tableIdentifiers.operatorsWithHighestQueryClickRatio,
            title: 'Operators with Highest Query/Click Ratio',
            columnDefinitions: {
                operatorName: {title: 'Operator', orderIndex: 1},
                ratio: {title: 'Ratio', orderIndex: 2},
            },
        },
    }), [])

    const dashboardComponents = useMemo(() => {
        const components = [
            {
                identifier: chartIdentifiers.overview,
                definitions: chartDefinitions[chartIdentifiers.overview],
                type: 'chart',
                size: {xs: 12, md: 12, xl: 12},
            },
            ...Object.values(tableIdentifiers).map((e) => ({
                identifier: e,
                type: 'table',
                definitions: tableDefinitions[e],
                size: {xs: 12, md: 6, xl: 4},
            })),
        ];
        components.sort((a, b) => componentOrder[a.identifier] - componentOrder[b.identifier]);
        return components;
    }, [chartDefinitions, componentOrder, tableDefinitions])


    /**
     * Fetches the component orders from the server
     * @type {(function(): Promise<void>)}
     */
    const getComponentOrders = useCallback(async () => {
        setLoadingComponentOrders(true);
        const cachedComponentOrders = await getCachedComponentOrders();
        if (!isMounted())
            return;
        if (cachedComponentOrders?.data) {
            setComponentOrder(cachedComponentOrders?.data ?? {})
        }
        setLoadingComponentOrders(false);
    }, [getCachedComponentOrders, isMounted])

    /**
     * As soon as the component is mounted, fetch the component orders
     */
    useEffect(() => {
        void getComponentOrders();
    }, [getComponentOrders])

    /**
     * Saves the component orders in the server
     * @type {(function(Record<string, int>): Promise<void>)}
     */
    const saveComponentsOrder = useCallback(async (componentsInOrder) => {
        const componentsOrder = Object.fromEntries(componentsInOrder.map((e, index) => ([e.key, index + 1])))
        setComponentOrder(componentsOrder ?? {})
        void saveCachedComponentOrders(componentsOrder);
    }, [saveCachedComponentOrders])

    /**
     * Opens the rearrange entries dialog
     * @type {(function(): void)}
     */
    const openRearrangeEntriesDialog = useCallback(() => {
        setRearrangeEntriesDialogState({
            open: true,
            close: () => setRearrangeEntriesDialogState(initialRearrangeEntriesDialogState),
            title: "Rearrange Dashboard Entries",
            entries: dashboardComponents.map(e => ({
                key: e.identifier,
                title: e.definitions?.title ?? "",
                size: e.size.xl,
            })),
            onRearrange: saveComponentsOrder,
        });
    }, [dashboardComponents, saveComponentsOrder])

    return (
        <>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                    <Box/>
                    <Button
                        size={'small'}
                        variant={'outlined'}
                        color={'primary'}
                        sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5}}
                        onClick={() => openRearrangeEntriesDialog()}
                    >
                        <LayersRounded/>
                        Rearrange components
                    </Button>
                </Box>
                <LookupAnalyticsDashboardViewSearchSection filters={query} setFilters={setQuery}/>
                {
                    loadingComponentOrders &&
                    <LoadingLottieAnimation width={'min(90%, 500px)'}/>
                }
                {
                    !loadingComponentOrders &&
                    <Grid2 container spacing={3}>
                        {
                            dashboardComponents.map(e => (
                                <Grid2 key={`${e.type}-${e.identifier}`} {...e.size}>
                                    {
                                        e.type === 'chart' &&
                                        <LookupAnalyticsDashboardViewChart
                                            filters={query}
                                            fetchData={fetchChartData}
                                            {...e.definitions}
                                        />
                                    }
                                    {
                                        e.type === 'table' &&
                                        <LookupAnalyticsDashboardViewTable
                                            filters={query}
                                            navigateOnClick={navigateOnClick}
                                            fetchData={fetchTableData}
                                            {...e.definitions}
                                        />
                                    }
                                </Grid2>
                            ))
                        }
                    </Grid2>
                }
            </Box>
            <RearrangeEntriesDialog
                open={rearrangeEntriesDialogState.open}
                close={rearrangeEntriesDialogState.close}
                title={rearrangeEntriesDialogState.title}
                entries={rearrangeEntriesDialogState.entries}
                onRearrange={rearrangeEntriesDialogState.onRearrange}
            />
        </>
    );
}

export default LookupAnalyticsDashboardView;